/**
 * index.ts — Importação de HTML no Studio (lado do aplicativo)
 *
 * O saneamento vem do MESMO arquivo usado pelas Edge Functions, reexportado
 * aqui para não existir uma segunda implementação no navegador. A limpeza feita
 * na tela é apenas para o usuário ver o resultado antes de enviar; a limpeza
 * que vale é a executada no servidor pela função `studio-import-html`.
 *
 * RELEASE: v1.1.0 - 2026-09-11 - HTML importado volta fatiado em seções
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (Studio, Fase 4)
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import type {
  StudioHtmlSectionData,
  StudioHtmlSplitReport,
} from '@/lib/studio/html/section';

export {
  detectStudioVariables,
  sanitizeStudioHtml,
  STUDIO_ALLOWED_ATTRS,
  STUDIO_ALLOWED_TAGS,
  STUDIO_FORBIDDEN_ELEMENTS,
} from '../../../../supabase/functions/_shared/studio/htmlSanitizer';

export type {
  StudioSanitizeReport,
  StudioSanitizeResult,
} from '../../../../supabase/functions/_shared/studio/htmlSanitizer';

/** Imagem embutida que virou arquivo guardado. */
export interface StudioSavedImage {
  asset_id: string;
  storage_path: string;
  size_bytes: number;
}

/** Resultado da importação conferida pelo servidor. */
export interface StudioImportResult {
  html: string;
  /**
   * Conteúdo já fatiado em seções editáveis. Cada item vira um bloco
   * `section.html` no documento — é o caminho normal de importação.
   */
  sections?: StudioHtmlSectionData[];
  /** Resumo do fatiamento (quantidade de seções e de campos). */
  sections_report?: StudioHtmlSplitReport;
  report: {
    removedElements: Record<string, number>;
    removedAttributes: Record<string, number>;
    slots: string[];
    variables: string[];
    externalImages: string[];
    inlineImages: number;
    savedImages: StudioSavedImage[];
    /** Avisos sobre imagens que não puderam ser guardadas. */
    droppedImages?: string[];
    /** Resumo do isolamento aplicado ao conteúdo (CSS escopado). */
    scope?: {
      removedImports: number;
      rewrittenGlobalRules: number;
      scopedStyleBlocks: number;
    };
  };
}

/** Resultado da conferência de um documento de blocos (JSON). */
export interface StudioJsonServerResult {
  document: string;
  report: StudioImportResult['report'];
}

/**
 * Envia o HTML colado para conferência no servidor.
 * Retorna o conteúdo limpo e o relatório do que foi ajustado.
 */
export async function importStudioHtml(
  html: string,
  templateId?: string | null,
): Promise<StudioImportResult> {
  const { data, error } = await supabase.functions.invoke('studio-import-html', {
    body: { format: 'html', html, template_id: templateId ?? null },
  });

  if (error) {
    logger.error('studio', 'Falha ao importar HTML', error);
    throw error;
  }

  return data as StudioImportResult;
}

/**
 * Envia o documento de blocos (já normalizado no navegador) para o servidor
 * guardar as imagens embutidas e devolver o JSON final.
 * Usa a MESMA função de importação, apenas com `format: 'json'`.
 */
export async function importStudioJson(
  documento: string,
  templateId?: string | null,
): Promise<StudioJsonServerResult> {
  const { data, error } = await supabase.functions.invoke('studio-import-html', {
    body: { format: 'json', document: documento, template_id: templateId ?? null },
  });

  if (error) {
    logger.error('studio', 'Falha ao importar documento JSON', error);
    throw error;
  }

  return data as StudioJsonServerResult;
}

export {
  parseStudioJsonDocument,
  serializeStudioDocument,
  StudioJsonImportError,
  STUDIO_JSON_LIMITS,
} from './jsonImport';

export type {
  StudioImportKind,
  StudioImportNote,
  StudioJsonImportResult,
} from './jsonImport';
